/* eslint-disable max-lines */
import { type TUser } from '@packages/utils/types'
import { get } from 'svelte/store'
import { accessTokenStore, userStore, isLoggedInStore, isLoggingInProcessStore } from '@packages/stores'
import { PassportLocalStorage } from '@packages/utils/enums'
import { analytics } from '@lib/mixpanel/mixpanel'
import { ethers } from '@credenza3/core-web-evm-ext'
import { clearAllQueryParams } from '@lib/queryString/queryString'
import { checkTransactionQuery, goToPayment } from '@pages/scanner/Scanner.service'
import { sdk } from '@lib/sdk/sdk'
import { getUser } from '@src/passport/user'
import { configStore, providerStore } from '@src/stores'

export const getGitbookAccessToken = async (): Promise<string> => {
  const { chainId, credenza } = get(configStore)
  const accessToken = get(accessTokenStore)
  if (!accessToken) throw new Error("Passport access token doesn't exist.")
  const result = await fetch(credenza.apiUrl + `/auth/gitbookToken/${chainId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  if (!result.ok) throw new Error(`${result.status} ${result.statusText}`)
  const res = await result.json()
  return res.gitbookToken
}

const checkCachedQrCode = (currentAddress: string) => {
  const cachedQrData = localStorage.getItem(PassportLocalStorage.QR_CODE)
  if (!cachedQrData) return
  try {
    const { date, sig } = JSON.parse(cachedQrData)
    const userAddress = ethers.verifyMessage(date, sig)
    if (userAddress.toLowerCase() !== currentAddress.toLowerCase()) throw new Error('Address mismatch')
  } catch {
    localStorage.removeItem(PassportLocalStorage.QR_CODE)
  }
}

export const retrieveCredenzaAccess = async () => {
  try {
    isLoggingInProcessStore.set(true)
    const config = get(configStore)
    const provider = await sdk.evm.getEthersProvider()
    providerStore.set(provider)

    const currentUserAddress = await (await provider.getSigner()).getAddress()
    const accessToken = sdk.getAccessToken()
    accessTokenStore.set(accessToken)
    checkCachedQrCode(currentUserAddress)

    const user: TUser = await getUser()
    userStore.set(user)
    isLoggedInStore.set(true)
    isLoggingInProcessStore.set(false)

    analytics.identify(user.sub)
    analytics.people.set({
      $email: user.email,
      $phone: user.phone,
      $name: user.name,
      chain: config.chainId,
      clientId: config.clientId,
      provider,
      address: currentUserAddress,
      location: window.location.href,
    })
    analytics.track('cp_init', {
      $email: user.email,
      $phone: user.phone,
      chain: config.chainId,
      provider,
      address: currentUserAddress,
      location: window.location.href,
      clientId: config.clientId,
    })
  } catch (err) {
    localStorage.removeItem(PassportLocalStorage.ACCESS_TOKEN)
    isLoggedInStore.set(false)
    isLoggingInProcessStore.set(false)
    userStore.set(null)
    accessTokenStore.set(null)
    console.error('Cannot init credenza user', err)
  }
}

export const handleTransactionInQuery = (isLoggedIn: boolean, openUI: () => void): void => {
  const params = Object.fromEntries(new URLSearchParams(location.search))
  const hasTransaction = checkTransactionQuery(params)
  if (!hasTransaction) return

  // if we have transaction, but we are not logged in
  if (!isLoggedIn) return void openUI()

  goToPayment({
    title: params.title,
    amount: +params.amount,
    tokenId: params.tokenId,
    contractAddress: params.contractAddress,
    contentTokenId: +params.contentTokenId,
    licenseType: +params.licenseType
  })
  void openUI()
  clearAllQueryParams()
}

export const getUserAddressBySub = async (sub: string): Promise<{ address: string }> => {
  const accessToken = get(accessTokenStore)
  if (!accessToken) throw new Error("Passport access token doesn't exist.")
  const config = get(configStore)

  const result = await fetch(`${config.credenza.evmApiUrl}/accounts/${sub}/assigned-address`, {
    method: 'get',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${get(accessTokenStore)}`,
    },
  })

  const json = await result.json()
  if (!result.ok) throw new Error(json.error)

  return json
}
